import { Module } from "vuex";
import * as firebase from "firebase/app";
import "firebase/firestore";
import moment from "moment";
import { EventsTypes } from "@/enums/EventsTypes";
import { Calendar } from "@/models/calendar/Calendar";
import { CreateEvent } from "@/models/calendar/CreateEvent";
import { EventsTypesMinor } from "@/enums/EventsTypesMinor";
import { v4 as uuidv4 } from "uuid";
import { PremiumServiceEventTypes } from "@/enums/PremiumServiceEventTypes";
import { getRegionName } from "@/utils/functionsRegion";
import { batchUpdate } from "@/utils/batchUpdate";
import { getDraftEventsByTimeframe } from "@/utils/eventService";
import { CalendarFilters } from "@/models/calendar/CalendarFilters";
import {serializer} from "@/api/shared/serializer";
import { getWhereIdInIdsExtended } from "@/utils/getWhereIdInIds";
import momentTz from 'moment-timezone';

const calenderModule: Module<any, any> = {
  namespaced: false,
  state: {
    mainCalendarFilters: new CalendarFilters(),
    calenderEvents: [],
    appointmentEvents: [],
    lifestyleEvents: [],
    latestCalenderEvents: [],
    onCalenderEventsUnsubscribe: null,
    onLatestCalenderEventsUnsubscribe: null,
    onLatestCalenderEventsWithWingsUnsubscribe: null,
    todaysEvents: [],
    currentResidentsCalendarEvents: [],
    currentCalenderEvent: null,
    currentCalenderRecurringEvents: [],
    subscribeEvent: [],
    calenderEventsUpcoming: [],
    calenderEventsResidentAndTime: [],
    recurringEventsUpdated: [],
    familyHubMemberEvents: [],
    lifestyleEventsReportData: null,
    residentsLifestyleEventsReportData: null,
    currentResidentCalendarEventsPast: [],
    calendarEventsPast: [],
    calendarScreenType: null,
    printableCalendarDate: null,
  },

  mutations: {
    setMainCalendarFilters(state: any, payload: any) {
      const filters = state.mainCalendarFilters

      if (payload?.hasOwnProperty('wingName')) {
        filters.wingName = payload.wingName;
      }
      if (payload?.hasOwnProperty('wingName')) {
        filters.wingId = payload.wingId;
      }
      if (payload?.hasOwnProperty('wingsIds')) {
        filters.wingsIds = payload.wingsIds;
      }
      if (payload?.hasOwnProperty('screenType')) {
        filters.screenType = payload.screenType;
      }
      if (payload?.hasOwnProperty('filters')) {
        filters.filters = payload.filters;
      }
      if (payload?.hasOwnProperty('locationEnabled')) {
        filters.locationEnabled = payload.locationEnabled;
      }
      if (payload?.hasOwnProperty('locationSearchValue')) {
        filters.locationSearchValue = payload.locationSearchValue;
      }
      if (payload?.hasOwnProperty('residentEnabled')) {
        filters.residentEnabled = payload.residentEnabled;
      }
      if (payload?.hasOwnProperty('residentSearchValue')) {
        filters.residentSearchValue = payload.residentSearchValue;
      }
      if (payload?.hasOwnProperty('lifestyleProgramsEnabled')) {
        filters.lifestyleProgramsEnabled = payload.lifestyleProgramsEnabled;
      }
      if (payload?.hasOwnProperty('lifestyleProgramsSearchValue')) {
        filters.lifestyleProgramsSearchValue = payload.lifestyleProgramsSearchValue;
      }
      if (payload?.hasOwnProperty('type')) {
        filters.type = payload.type;
      }

      state.mainCalendarFilters = filters;
    },
    setCurrentWing(state: any, payload: object) {
      state.currentWing = payload;
    },
    setCalenderEvents(state: any, payload: []) {
      state.calenderEvents = payload;
    },
    setAppointmentEvents(state: any, payload: []) {
      state.appointmentEvents = payload;
    },
    setCurrentCalenderEvent(state: any, payload: object) {
      state.currentCalenderEvent = payload;
    },
    setCurrentCalenderRecurringEvents(state: any, payload: object) {
      state.currentCalenderRecurringEvents = payload;
    },
    setLatestCalenderEvents(state: any, payload: object) {
      state.latestCalenderEvents = payload;
    },
    setLifestyleEvents(state: any, payload: object) {
      state.lifestyleEvents = payload;
    },
    setLifestyleEventsReportData(state: any, payload: object) {
      state.lifestyleEventsReportData = payload;
    },
    setResidentsLifestyleEventsReportData(state: any, payload: object) {
      state.residentsLifestyleEventsReportData = payload;
    },
    setTodaysEvents(state: any, payload: object) {
      state.todaysEvents = payload;
    },
    setCurrentResidentsCalendar(state: any, payload: object) {
      state.currentResidentsCalendarEvents = payload;
    },
    setRecurringEventsUpdated(state: any, payload: object) {
      state.recurringEventsUpdated = payload;
    },
    setCalenderEventsUpcoming(state: any, payload: []) {
      state.calenderEventsUpcoming = payload;
    },
    setPastCalenderEvents(state: any, payload: []) {
      state.calendarEventsPast = payload;
    },
    setResidentEventsMonth(state: any, payload: []) {
      state.residentEventsMonth = payload;
    },
    setResidentVideoCallsMonth(state: any, payload) {
      state.residentVideoCallsMonth = payload;
    },
    setFamilyHubMemberEvents(state: any, payload: []) {
      state.familyHubMemberEvents = payload;
    },
    setPastEventsByResident(state: any, payload: []) {
      state.currentResidentCalendarEventsPast = payload;
    },
    setPrintableCalendarDate(state: any, payload: Date) {
      state.printableCalendarDate = payload;
    }
  },

  actions: {
    setMainCalendarFilters({ commit }, filtersObj) {
      commit("setMainCalendarFilters", filtersObj);
    },

    async getCalenderEvents({ commit, dispatch }, data) {
      console.log('getCalenderEvents() data: ', data)
      const fromDate = data ? data.start : moment().add(-25, "days").toDate();
      const toDate = data ? data.end : moment().add(60, "days").toDate();
      const facilityId = this.state.authModule.currentFacility;
      const isOptInLifestylePrograms = this.state.facilityModule.selectedFacility.isOptInLifestylePrograms;

      const additionalWhere = [{ field: 'start', operator: '>=', value: fromDate },
         { field: 'start', operator: '<=', value: toDate },
         { field: 'facilityId', operator: '==', value: facilityId }];

      const calendarEvents = (await getWhereIdInIdsExtended('type', Object.values(EventsTypes), 'calender', this.state.authModule.currentFacility, additionalWhere))
      .map(x=>({startDate: moment(x.start.seconds * 1000).toDate(), endDate: moment(x.end.seconds * 1000).toDate(), ...x} ))

      if(!this.state.additionalServicesModule.additionalServices.length) {
        await dispatch("getAdditionalServices")
      }

      const lspCategories = this.state.socialClubModule.lifestyleProgramCategories;
      const lifestylePrograms = this.state.socialClubModule.socialClubs;
      const additionalServices = this.state.additionalServicesModule.additionalServices;
      const residents = this.state.residentModule.residentsShortData;
      const residentNames = residents.map((doc: any) => doc.name);
      const residentIds = residents.map((doc: any) => doc.id);
      const serviceRequests = this.state.additionalServicesModule.serviceRequests
        .filter((x: any) => additionalServices.some((s: any) => s.id === x.serviceId));

      const resList = calendarEvents.map(x => {
        const thisRes = x
        thisRes.id = x.id;
        thisRes.lifestyleProgramsNames = [];
        thisRes.serviceRequestsResidentsNames = [];
        thisRes.residentsMembersIds = [];
        thisRes.location = thisRes.location || '';
        thisRes.wing = thisRes.wing || '';
        thisRes.attendees = thisRes.attendees || [];
        thisRes.attendeesNames = residents.filter(x => thisRes.attendees.some(a => a === x.id)).map(x => x.name)
        thisRes.parentEventId = thisRes?.parentEventId || '';

        if (thisRes.type === EventsTypes.LifestylePrograms && thisRes.invitees?.length) {
          // Set lifestyleProgramsNames
          thisRes.lifestyleProgramsNames = lifestylePrograms.filter(prog => thisRes.invitees.includes(prog.id)).map((prog: any) => prog.name);

          // Set lifestyleProgramCategoryColor
          const lifestyleProgramId = thisRes.invitees[0];
          const allInvited = lifestyleProgramId.toLowerCase() === 'all'
          if (allInvited || !isOptInLifestylePrograms) {
            thisRes.serviceRequestsResidentsNames = residentNames
            thisRes.serviceRequestsResidentsIds = residentIds
            thisRes.residentsMembersIds = residentIds
          }

          if (lifestyleProgramId && !allInvited) {
            const lifestyleProgram: any = lifestylePrograms.find(prog => prog.id === lifestyleProgramId);
            if (lifestyleProgram) {
              const lifestyleProgramCategory: any = lspCategories.find((cat: any) => cat.id === lifestyleProgram.lifestyleProgramCategoryId);
              if (lifestyleProgramCategory) {
                thisRes.lifestyleProgramCategoryId = lifestyleProgramCategory.id;
                thisRes.lifestyleProgramCategoryColor = lifestyleProgramCategory.color;
                thisRes.lifestyleProgramCategoryName = lifestyleProgramCategory.name;
              }
            }

          // Set residentsMembersIds
            thisRes.residentsMembersIds = additionalServices
            .filter((svc: any) => thisRes.invitees.includes(svc.id))
            .map(x => x.membersIds)
            .reduce((acc, curr) => acc.concat(curr), []);
          }
        }

        if (thisRes.type === EventsTypes.PremiumServices && thisRes.invitees?.length) {
          // Set serviceRequestsResidentsNames
          const serviceRequestsResidentsIds = serviceRequests.filter((req: any) => thisRes.invitees.includes(req.serviceId) && req.residentId?.length).map(x => x.residentId)
          thisRes.serviceRequestsResidentsIds = serviceRequestsResidentsIds
          thisRes.serviceRequestsResidentsNames = residents.filter(x => serviceRequestsResidentsIds.some(r => r === x.id)).map(x => x.name.trim())
            .reduce((uniqueNames, name) => {
              if (!uniqueNames.includes(name)) {
                uniqueNames.push(name);
              }
              return uniqueNames;
            }, []);

          // Set residentsMembersIds
          thisRes.residentsMembersIds = additionalServices
          .filter((svc: any) => thisRes.invitees.includes(svc.id))
          .map(x => x.membersIds)
          .reduce((acc, curr) => acc.concat(curr), []);

          // Set conciergeServicesCategoryColor
          const conciergeServiceId = thisRes.invitees[0];
          const allInvited = conciergeServiceId.toLowerCase() === 'all'

          if (conciergeServiceId && !allInvited) {
            const conciergeService: any = additionalServices.find(service => service.id === conciergeServiceId);

            if (conciergeService) {
              const conciergeServiceCategory: any = lspCategories.find((cat: any) => cat.id === conciergeService.categoryId);
              if (conciergeServiceCategory) {
                thisRes.lifestyleProgramCategoryId = conciergeServiceCategory.id;
                thisRes.lifestyleProgramCategoryColor = conciergeServiceCategory.color;
                thisRes.lifestyleProgramCategoryName = conciergeServiceCategory.name;
              }
            }
          }
        }

        return thisRes;
      });

      commit("setCalenderEvents", resList);

      dispatch("subscribeOnCalenderEvents", {fromDate, toDate});

      return resList
    },

    async getAppointmentEvents({ commit }, range) {
      const fromDate = range ? range.start : moment().add(-25, "days").toDate();
      const toDate = range ? range.end : moment().add(60, "days").toDate();
      const facilityId = this.state.authModule.currentFacility;

      const additionalServices: any[] = (await firebase.firestore().collection("additionalServices")
      .where("facilityId", "==", this.state.authModule.currentFacility)
      .where("byAppointment", "==", true)
      .get()).docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const calendarEventsSnapshot = await firebase.firestore().collection("calender")
        .where("facilityId", "==", facilityId)
        .where("start", ">=", fromDate)
        .where("start", "<=", toDate)
        .where("type", "==", EventsTypes.PremiumServices).get()

      const resList = calendarEventsSnapshot.docs.map(x => {
        const thisRes = x.data();
        thisRes.id = x.id;
        return thisRes;
      }).filter(thisRes => {
        if (thisRes.invitees?.length) {
          const premiumServiceId = thisRes.invitees[0];
          return additionalServices.some(service => service.id === premiumServiceId);
        }
        return false;
      });

      commit("setAppointmentEvents", resList);
    },


    async deleteAppointmentEventsByRequest(_, request) {
      if(request && request?.byAppointment) {
        await firebase
          .firestore()
          .collection("calender")
          .where("facilityId", "==", this.state.authModule.currentFacility)
          .where("invitees", "array-contains", request.serviceId)
          .where("conciergeRequestId", "==", request.id)
          .where("start", "==", request.startTime)
          .where("end", "==", request.endTime)
          .get()
          .then(async (querySnapshot) => {
            const events = querySnapshot.docs.map(x => ({ id: x.id, ref: x.ref }));
            for(const event of events) {
              await event.ref.delete();
            }
          })
      }
    },

    async getCalenderEventsByFamilyHubMemberId(
      { commit },
      { familyHubMemberId, eventType }
    ) {
      const calenderEvents = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("type", "==", eventType)
        .where("familyGoing", "array-contains", familyHubMemberId);

      await calenderEvents.get().then(querySnapshot => {
        const resList: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          resList.push(thisRes);
        });
        commit("setFamilyHubMemberEvents", resList);
      });
    },

    async getCalenderEventsUpcoming({ commit }) {
      console.log('getCalenderEventsUpcoming')
      const fromDate = moment().add(-1, "months").toDate();

      const toDate = moment().add(1, "months").toDate();

      const types = [...Object.values(EventsTypes), EventsTypesMinor.ScheduledCall]

     const additionalWhere = [{ field: 'start', operator: '>=', value: fromDate }, { field: 'start', operator: '<=', value: toDate }];

      const calenderEvents = (await getWhereIdInIdsExtended('type', types, 'calender', this.state.authModule.currentFacility, additionalWhere))
      .map(x=>({startDate: moment(x.start.seconds * 1000).toDate(),endDate: moment(x.end.seconds * 1000).toDate(), ...x} ))

      commit("setCalenderEventsUpcoming", calenderEvents);
    },

    async getPastCalendarEvents({ commit }) {
      const fromDate = moment()
        .add(-1, "months")
        .toDate();

      const toDate = moment()
        .add(0, "days")
        .toDate();

      const calenderEvents = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">=", fromDate)
        .where("start", "<=", toDate)
        .where("type", "in", [
          EventsTypes.LifestylePrograms,
          EventsTypes.PremiumServices,
          EventsTypes.ConciergeRequest,
          EventsTypes.Medical,
          EventsTypes.Concierge
        ]).orderBy("start", "desc")

      await calenderEvents.get().then(querySnapshot => {
        const resList: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          if (!thisRes.attendeesIds?.length) {
            thisRes.id = x.id;
            resList.push(thisRes);
          }
        });
        commit("setPastCalenderEvents", resList);
      });
    },

    async getLatestCalenderEvents({ dispatch }, { limit, facilityId, selectedWings }) {
      if (selectedWings.length > 10) {
        const result = [];

        for (let i = 0; i < selectedWings.length; i += 10) {
          result.push(selectedWings.slice(i, i + 10));
        }

        for (let i = 0; i < result.length; i++) {
          const wings = result[i];
          dispatch("subscribeOnLastsEvents", { limit, facilityId, wings })
        }

      } else {
        const wings = selectedWings
        dispatch("subscribeOnLastsEvents", { limit, facilityId, wings })
      }
    },

    async subscribeOnLastsEvents({ dispatch, state }, { limit, facilityId, wings }) {
      const calenderEventsQuery = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", facilityId)
        .where("isPublished", "==", true)
        .where("start", "<=", new Date())
        .where("wingsIds", "array-contains-any", wings)
        .orderBy("start", "desc")
        .limit(limit);

      const calenderEventsWithEmptyWingQuery = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", facilityId)
        .where("wingsIds", "==", [])
        .where("isPublished", "==", true)
        .where("start", "<=", new Date())
        .orderBy("start", "desc")
        .limit(limit);

      state.onLatestCalenderEventsUnsubscribe = calenderEventsQuery
        .onSnapshot((snapshot) => {
          const data = serializer.querySnapshotToDocuments(snapshot);
          const result = [];

          data.forEach((thisRes: any) => {
            thisRes.start = moment(thisRes.start.seconds * 1000).toDate();
            thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
            thisRes.scheduledPublishDateTime = thisRes?.scheduledPublishDateTime ? moment(thisRes.scheduledPublishDateTime.seconds * 1000).toDate() : null;
            result.push(thisRes);
          });

          dispatch('setLastsEvents', result)
        });

      state.onLatestCalenderEventsWithWingsUnsubscribe = calenderEventsWithEmptyWingQuery
        .onSnapshot((snapshot) => {
          const data = serializer.querySnapshotToDocuments(snapshot);
          const result = [];

          data.forEach((thisRes: any) => {
            thisRes.start = moment(thisRes.start.seconds * 1000).toDate();
            thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
            thisRes.scheduledPublishDateTime = thisRes?.scheduledPublishDateTime ? moment(thisRes.scheduledPublishDateTime.seconds * 1000).toDate() : null;
            result.push(thisRes);
          });

          dispatch('setLastsEvents', result)
        });
    },

    async subscribeOnCalenderEvents({ dispatch, state }, { fromDate, toDate }) {

      if(state.onCalenderEventsUnsubscribe) return

      console.log(`subscribeOnCalenderEvents from ${fromDate} to ${toDate}`)

      const calenderEventsQuery = firebase.firestore().collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">=", fromDate)
        .where("start", "<=", toDate)

      state.onCalenderEventsUnsubscribe = calenderEventsQuery
        .onSnapshot((snapshot) => {
          snapshot.docChanges().forEach( (change) => {
            if (change.type === "removed") {
              console.log('removed event', change.doc.id)
              state.calenderEvents = state.calenderEvents.filter((x: any) => x.id !== change.doc.id)
            }
          })

        });

    },

    async setLastsEvents({ dispatch, commit, state }, data) {
      let resList: Array<any> = [];
      const stateData: any = state.latestCalenderEvents

      if(data && data.length) {
        if(!stateData.length) {
          resList = data;
        } else {
          for (let i = 0; i < stateData.length; i++) {
            const object = stateData[i];
            if (data.every(x => x.wingsIds && x.wingsIds.length)) {
              if (object.wingsIds && object.wingsIds.length && !data.some(element => element.id === object.id)) {
                stateData.splice(i, 1);
                i--;
              }
            } else {
              if (object.wingsIds && !object.wingsIds.length && !data.some(element => element.id === object.id)) {
                stateData.splice(i, 1);
                i--;
              }
            }
          }

          const uniqueIds = new Set();
          resList = [...stateData, ...data].filter(obj => {
            if (!uniqueIds.has(obj.id)) {
              uniqueIds.add(obj.id);
              return true;
            }
            return false;
          });
        }
      }

      resList = resList.sort((a: any, b: any) => moment(a.start).diff(moment(b.start)));
      commit("setLatestCalenderEvents", resList);
    },

    async getSocialEvents({ commit }, { startDate, endDate, loadAdditionalServices = false }) {
      const calenderEvents = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("type", "in", [...Object.values(EventsTypes)])
        .where("start", ">=", startDate)
        .where("start", "<=", endDate)
        .orderBy("start", "desc")
        .limit(50);

      let additionalServices: any = []

      if (loadAdditionalServices) {
        additionalServices = (await firebase
          .firestore()
          .collection("additionalServices")
          .where("facilityId", "==", this.state.authModule.currentFacility)
          .where("type", "==", PremiumServiceEventTypes.EventBased)
          .get()).docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      let lifestylePrograms: any = []

      lifestylePrograms = (await firebase
        .firestore()
        .collection("socialClubs")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .get()).docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const allServicesAndPrograms = [...additionalServices, ...lifestylePrograms];

      await calenderEvents.get().then(querySnapshot => {
        const resList: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          thisRes.start = moment(thisRes.start.seconds * 1000).toDate();
          thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
          thisRes.scheduledPublishDateTime = thisRes?.scheduledPublishDateTime? moment(thisRes.scheduledPublishDateTime.seconds * 1000).toDate(): null;

          if (thisRes.invitees) {
            thisRes.inviteesDetails = allServicesAndPrograms.filter(x => thisRes.invitees.indexOf(x.id) >= 0).map(x => ({ id: x.id, name: x.name }))
          }
          resList.push(thisRes);
        });
        commit("setLatestCalenderEvents", resList);
      });
    },

    async getLifestyleEvents({ commit }, { startDate, endDate }) {
      const calenderEvents = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("type", "==", EventsTypes.LifestylePrograms)
        .where("start", ">=", startDate)
        .where("start", "<=", endDate)
        .orderBy("start", "desc");

      await calenderEvents.get().then(querySnapshot => {
        const resList: Array<any> = [];

        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          thisRes.start = moment(thisRes.start.seconds * 1000).toDate();
          thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
          thisRes.scheduledPublishDateTime = thisRes?.scheduledPublishDateTime? moment(thisRes.scheduledPublishDateTime.seconds * 1000).toDate(): null;
          resList.push(thisRes);
        });
        commit("setLifestyleEvents", resList);
      });
    },

    async getLifestyleEventsReportData({ commit },{ startDate = moment(), endDate = moment() }) {

      const fromDate = moment(startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
      const toDate =  moment(endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')

      const response = await firebase.app().functions(getRegionName()).httpsCallable("reports-getLifestyleEventsReportData")({
        startDate: fromDate,
        endDate:  toDate,
        facilityId: this.state.authModule.currentFacility,
        clientTimeZone:  momentTz.tz.guess()
      })

      const report = response.data.data

      report.lifestyleEvents.map((event: any) => {
        event.start = moment(event.start).toDate()
        event.end = moment(event.end).toDate()
        return event;
      });

      commit("setLifestyleEventsReportData", report);
    },

    async getResidentsLifestyleEventsReportData(
      { commit },
      { startDate, endDate }
    ) {
      const events = await firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("type", "==", EventsTypes.LifestylePrograms)
        .where("start", ">=", startDate)
        .where("start", "<=", endDate)
        .orderBy("start", "desc")
        .get();

      const residents = await firebase
        .firestore()
        .collection("residents")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .get();

      const report = {
        residents: []
      };

      if (events.docs.length > 0) {
        //get data from query
        let residentsData = residents.docs.map((x: any) => {
          const r = x.data();
          r.id = x.id;
          return r;
        });
        const eventsData = events.docs.map((event: any) => {
          event = event.data();
          event.start = moment(event.start.seconds * 1000).toDate();
          event.end = moment(event.end.seconds * 1000).toDate();
          return event;
        });

        //set report data for each resident
        residentsData.forEach(resident => {
          //get lifestyle events
          const residentEvents: any[] = eventsData.filter(
            x => x.attendeesIds && x.attendeesIds.indexOf(resident.id) !== -1
          );
          resident.eventsCount = residentEvents.length;

          //get virtual lifestyle events
          resident.virtualEventsCount = residentEvents.filter(
            x => x.isVirtualEvent
          ).length;

          //get not virtual lifestyle events
          resident.notVirtualEventsCount = residentEvents.filter(
            x => !x.isVirtualEvent
          ).length;

          //normalize status
          resident.statusDisplay = resident.status.replace(" Resident", "");

          //get average number of events per day per selected date range
          const result = residentEvents.reduce(function (totals, event) {
            const day = moment(event.start).format("DD/MM/YYYY");
            !totals[day] ? (totals[day] = 1) : (totals[day] = totals[day] + 1);

            return totals;
          }, {});
          const dayEventsCountArr: number[] = Object.values(result);
          const sum = dayEventsCountArr.reduce((a, b) => a + b, 0);
          resident.eventsPerDay =
            dayEventsCountArr.length > 0
              ? Math.ceil(sum / dayEventsCountArr.length)
              : 0;
        });

        // sort residents by lifestyle events number
        residentsData = residentsData.sort((a, b) => {
          return a.eventsCount - b.eventsCount;
        });

        //set report data
        report.residents = residentsData.map((x) => {
          return {
            id: x.id,
            statusDisplay: x.statusDisplay,
            name: x.name,
            eventsCount: x.eventsCount,
            eventsPerDay: x.eventsPerDay,
            virtualEventsCount: x.virtualEventsCount,
            notVirtualEventsCount: x.notVirtualEventsCount
          }
        })
      }

      commit("setResidentsLifestyleEventsReportData", report);
    },

    async getTodaysEvents({ commit }, { limit, facilityId }) {
      const startdate = moment().toDate();
      const enddate = moment()
        .add(1, "days")
        .toDate();

      const calenderEvents = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", facilityId)
        .where("type", "in", [...Object.values(EventsTypes)])
        .orderBy("start", "desc")
        .limit(limit)
        .where("start", ">", startdate)
        .where("start", "<", enddate);
      await calenderEvents.get().then(querySnapshot => {
        const resList: Array<any> = [];

        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.scheduledPublishDateTime = thisRes?.scheduledPublishDateTime? moment(thisRes.scheduledPublishDateTime.seconds * 1000).toDate(): null;
          const start = moment(thisRes.start.seconds * 1000).toDate();
          thisRes.id = x.id;
          thisRes.start = start;
          thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
          resList.unshift(thisRes);
        });
        commit("setTodaysEvents", resList);
      });
    },

    async getPastEventsByResident({ commit }, { residentId }) {
      const fromDate = moment()
        .subtract(3, "month")
        .toDate();
      const toDate = moment().toDate();
      const resList: Array<any> = [];

      const calenderEvents = await firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">=", fromDate)
        .where("start", "<=", toDate)
        .where("type", "in", [...Object.values(EventsTypes)])
        .where("attendeesIds", "array-contains", residentId)
        .get().then(querySnapshot => {
          querySnapshot.docs.forEach(x => {
            const thisRes = x.data();
            thisRes.id = x.id;
            thisRes.start = moment(thisRes.start.seconds * 1000).toDate();
            thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
            thisRes.scheduledPublishDateTime = thisRes?.scheduledPublishDateTime? moment(thisRes.scheduledPublishDateTime.seconds * 1000).toDate(): null;

            resList.push(thisRes);
          });
        });

      const calenderEventsNotice = await firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">=", fromDate)
        .where("start", "<=", toDate)
        .where("type", "in", [EventsTypes.Notice])
        .get().then(querySnapshot => {
          querySnapshot.docs.forEach(x => {
            const thisRes = x.data();
            thisRes.id = x.id;
            thisRes.start = moment(thisRes.start.seconds * 1000).toDate();
            thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
            thisRes.scheduledPublishDateTime = thisRes?.scheduledPublishDateTime? moment(thisRes.scheduledPublishDateTime.seconds * 1000).toDate(): null;

            resList.push(thisRes);
          });
        });

      commit("setPastEventsByResident", resList);
    },

    async getUpcomingEventsByResident({ commit }, { residentId }) {
      console.log('getUpcomingEventsByResident', residentId)
      if(!residentId) return
      
      const residentServicesApproveList: Array<any> = [];
      const residentServicesApprove = firebase
        .firestore()
        .collection("additionalServices")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("membersIds", "array-contains", residentId);
      await residentServicesApprove.get().then(querySnapshot => {
        querySnapshot.docs.forEach(x => {
          const thisRes = x.id;
          residentServicesApproveList.push(thisRes);
        });
      });

      const residentLifestyleProgramsList: Array<any> = []
      const residentLifestylePrograms = firebase
        .firestore()
        .collection("socialClubs")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("members", "array-contains", residentId);
      await residentLifestylePrograms.get().then(querySnapshot => {
        querySnapshot.docs.forEach(x => {
          const thisRes = x.id;
          residentLifestyleProgramsList.push(thisRes);
        });
      });

      const startdate = moment().startOf('date').toDate();
      const enddate = moment()
        .add(7, "days")
        .toDate();

      const calenderEvents = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">=", startdate)
        .where("start", "<=", enddate)
        .orderBy("start")
        .limit(100);

      await calenderEvents.get().then(querySnapshot => {
        const resList: Array<any> = [];
        querySnapshot.docs.filter(x => {
          const thisRes = x.data();
          if (
            //thisRes.type != "Visit" &&
            (
              thisRes.invitees?.some(item => residentServicesApproveList.includes(item)) &&
              thisRes.going.includes(residentId)
            )
            ||
            (
              thisRes.type == EventsTypes.LifestylePrograms &&
              !this.state.facilityModule.selectedFacility.isOptInLifestylePrograms
            )
            ||
            (
              thisRes.type == EventsTypes.LifestylePrograms &&
              this.state.facilityModule.selectedFacility.isOptInLifestylePrograms &&
              (
                (!thisRes.invitees.includes('All') && (thisRes.going.includes(residentId) || residentLifestyleProgramsList.includes(thisRes.invitees))) ||
                (thisRes.invitees.includes('All') && residentLifestyleProgramsList.length > 0)
              )
            )
            ||
            (
              thisRes.type == EventsTypesMinor.ScheduledCall &&
              thisRes.going.includes(residentId)
            )
            ||
            (
              thisRes.type == EventsTypesMinor.ConciergeRequest &&
              thisRes.going &&
              thisRes.going.includes(residentId)
            )
            ||
            (thisRes.type == EventsTypesMinor.Notice)
          ) {
            const start = moment(thisRes.start.seconds * 1000).toDate();
            thisRes.id = x.id;
            thisRes.header = moment(start).isSame(new Date(), "day")
              ? "Today"
              : "This Week";
            thisRes.start = moment(thisRes.start.seconds * 1000).toDate();
            thisRes.end = moment(thisRes.end.seconds * 1000).toDate();

            resList.push(thisRes);
          }
        });
        commit("setCurrentResidentsCalendar", resList);
      });
    },

    async getCalenderEvent({ commit, dispatch }, id) {
      try {
        const residents = firebase
          .firestore()
          .collection("calender")
          .doc(id);
        residents.get().then(querySnapshot => {
          const currentRes = querySnapshot.data();
          if (currentRes != null) {
            currentRes.id = querySnapshot.id;
            currentRes.start = moment(currentRes.start.seconds * 1000).toDate();
            currentRes.end = moment(currentRes.end.seconds * 1000).toDate();
            currentRes.scheduledPublishDateTime = currentRes?.scheduledPublishDateTime ? moment(currentRes.scheduledPublishDateTime.seconds * 1000).toDate() : null
            
            commit("setCurrentCalenderEvent", currentRes);

            if (currentRes.invitees[0] && currentRes.type == EventsTypes.PremiumServices) {
              dispatch("getAdditionalService", currentRes.invitees[0]);
            }
          }
        });
      } catch (e) {
        console.error(e);
      }
    },

    async subscribeCalenderEvent({ commit }, id) {
      this.state.subscribeEvent = firebase
        .firestore()
        .collection("calender")
        .doc(id)
        .onSnapshot(doc => {
          const document = doc.data();
          if (doc.exists) {
            document.id = doc.id;
            document.start = moment(document.start.seconds * 1000).toDate();
            document.end = moment(document.end.seconds * 1000).toDate();
          }
          commit("setCurrentCalenderEvent", document);
          console.group("Firebase realtime event");
          console.log(document.participants);
          console.groupEnd();
        });
    },

    async unsubscribeCalenderEvent(context) {
      this.state.subscribeEvent();
    },

    async getCalenderRecurringEvents({ commit }, id) {
      try {
        const calender = firebase
          .firestore()
          .collection("calender")
          .doc(id);
        calender.get().then(querySnapshot => {
          const currentRes = querySnapshot.data();
          if (
            currentRes != null &&
            currentRes.parentEventId != null &&
            currentRes.parentEventId != ""
          ) {
            const calenders = firebase
              .firestore()
              .collection("calender")
              .where("facilityId", "==", this.state.authModule.currentFacility)
              .where("parentEventId", "==", currentRes.parentEventId)
              .orderBy("start");
            calenders.get().then(querySnapshot => {
              const resList: Array<any> = [];

              querySnapshot.docs.forEach(x => {
                const thisRes = x.data();
                thisRes.id = x.id;
                thisRes.start = moment(thisRes.start.seconds * 1000).toDate();
                thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
                thisRes.scheduledPublishDateTime = thisRes?.scheduledPublishDateTime ? moment(thisRes.scheduledPublishDateTime.seconds * 1000).toDate(): null
                
                resList.push(thisRes);
              });

              commit("setCurrentCalenderRecurringEvents", resList);
            });
          }
        });
      } catch (e) {
        console.error(e);
      }
    },

    clearCurrentCalenderRecurringEvents({ commit }) {
      commit("setCurrentCalenderRecurringEvents", null);
    },

    async createNewCalenderEvent({ getters, dispatch }, newEvent) {
      newEvent.facilityId = this.state.authModule.currentFacility;
      newEvent.organiserName = getters.user.name;
      newEvent.channelId = newEvent.channelId ? newEvent.channelId : null;
      newEvent.startISOString = moment(newEvent.start.seconds * 1000).toISOString()
      newEvent.createdByStaffId = newEvent.createdByStaffId || this.state.authModule.user.id

      return firebase
        .firestore()
        .collection("calender")
        .add({ ...newEvent })
        .then(async createdDocRef => {
          //send email to external host of event
          if (newEvent.isExternallyHosted && newEvent.isPublished) {
            await dispatch('sendEmailToExternalHostOfEvent', { eventId: createdDocRef.id, parentEventId: null })
          }

          dispatch("getCalenderEvents");
          return createdDocRef.id;
        });
    },

    async updateCalenderEvent({ commit, dispatch }, eventData) {
      const event = eventData.event;

      if(event.isPublished === true && event.scheduledPublishDateTime){
        event.scheduledPublishDateTime = null
      }

      const prevEventData = (
        await firebase
          .firestore()
          .collection("calender")
          .doc(event.id).get()).data();

          if (event.likes && Array.isArray(event.likes)) {
            event.likes = event.likes.map((like: any) => ({
                ...like,
                date: moment(like.date.seconds * 1000).format("YYYY-MM-DDTHH:mm:ss")
            }));
          }

      if (event.isRecurring) {
        const recurringRange = eventData.recurringRange;

        //if event changed from single event to recurring event
        if (!prevEventData.isRecurring) {
          console.log('changing from single event to recurring event')
          //delete single event
          return await firebase
            .firestore()
            .collection("calender")
            .doc(event.id).delete().then(async () => {

              delete event.id
              event.parentEventId = uuidv4();
              event.startDate = recurringRange.start;
              event.endDate = recurringRange.end;
              event.duration = recurringRange.duration;

              console.log("deleted single event");
              //add recurring events
              return await this.dispatch("getRecurringEvents", {
                eventFormData: event,
                noEndDate: event.noEndDate,
                invitees: event.invitees,
                likes: event.likes,
                monthlyRepeatOn: event.monthlyRepeatOn
              })
                .then(async events => {
                  return await dispatch("createNewCalenderEvents", {
                    events: events,
                    isUpdateRecurringEventsAction: true
                  }).then(async (createdEvents) => {
                    console.log('created recurring events')

                    await dispatch("getCalenderRecurringEvents", createdEvents[0].id)

                    return await dispatch("getCalenderEvent", createdEvents[0].id).then(() => {
                      return createdEvents[0].id
                    })

                  })
                })
            })
        }
        else {
          console.log('updating recurring events')

          //update events
          const query = firebase
            .firestore()
            .collection("calender")
            .where("parentEventId", "==", event.parentEventId)
            .where("facilityId", "==", this.state.authModule.currentFacility)

          let prevRecurringEvents = [];

          return await query
            .get()
            .then(recurringEvents => {
              prevRecurringEvents = serializer.querySnapshotToDocuments(recurringEvents);

              const batch = firebase.firestore().batch();
              recurringEvents.docs.forEach(doc => {
                batch.delete(doc.ref);
              });
              batch.commit();
            })
            .then(async () => {
              event.startDate = recurringRange.start;
              event.endDate = recurringRange.end;
              event.duration = recurringRange.duration;
              event.eventType = event.type;

              return await dispatch("getRecurringEvents", {
                eventFormData: event,
                noEndDate: event.noEndDate,
                invitees: event.invitees,
                likes: event.likes,
                monthlyRepeatOn: event.monthlyRepeatOn
              }).then(async rEvents => {

                rEvents.forEach(item => {
                  const eventStart = moment(item.start).format("YYYY-MM-DD");
                  const prevEvent = prevRecurringEvents.find(x => moment(x.start.seconds * 1000).format("YYYY-MM-DD") === eventStart);

                  item.attendees = prevEvent?.attendees ? prevEvent?.attendees : [];
                  item.attendeesIds = prevEvent?.attendeesIds ? prevEvent?.attendeesIds : [];
                  item.galleryPhotos = prevEvent?.galleryPhotos ? prevEvent?.galleryPhotos : [];
                })

                //send email to external host of event
                if (rEvents[0]?.isExternallyHosted && rEvents[0].isPublished && (rEvents[0]?.hostEmailAddress != prevEventData?.hostEmailAddress || rEvents[0].isPublished != prevEventData.isPublished)) {
                  await dispatch('sendEmailToExternalHostOfEvent', { eventId: null, parentEventId: rEvents[0].parentEventId })
                }

                return await dispatch("createNewCalenderEvents", {
                  events: rEvents,
                  isUpdateRecurringEventsAction: true
                }).then(async (createdEvents) => {

                  if(event.office365EventId?.length > 0){
                      const params = {
                        id: event.office365EventId,
                        subject: event.name,
                        roomName: event.office365RoomName,
                        startDateTime: moment(rEvents[0].start).format('YYYY-MM-DDTHH:mm:ss'),
                        endDateTime: moment(rEvents[0].end).format('YYYY-MM-DDTHH:mm:ss'),
                        attendees: [ { email: this.state.office365Module.office365TokenData.email, name: this.state.office365Module.office365TokenData.name }],
                        office365ResourceEmail: event?.office365Email || null,
                        facilityId: event.facilityId,
                        recurrence: {
                            type: event.recurringType,
                            repeatType: event?.recurringRepeatType,
                            repetitionsDays: event?.recurringRepetitionsDays,
                            monthlyRepeatOn: event.monthlyRepeatOn,
                            interval: event.recurringRepetitions,
                            startDateTime: recurringRange.start,
                            endDateTime: recurringRange.end
                        }
                    }

                    await dispatch('updateMsEvent', params).then(response => {
                        if (response.data?.status !== 200) {
                            console.error('Error updating MS event', response.data)
                        }
                    });
                  }
                  await dispatch("getCalenderRecurringEvents", createdEvents[0].id)

                  return await dispatch("getCalenderEvent", createdEvents[0].id).then(() => {
                    console.log('updated recurring upcoming events')
                    return createdEvents[0].id
                  })
                })

              })
            })
        }

      } 
      else {
        //if event changed from recurring to non-recurring
        if (prevEventData.isRecurring) {
          console.log('changing from recurring to single event')

          //delete recurring events
          return await this.dispatch('deleteCalenderEventSeries', { parentEventId: event.parentEventId, deleteUpcomingEvents: false })
            .then(async (result) => {
              console.log('deleted event series by parent event id: ' + event.parentEventId)
              //add single event
              const singleEvent = { ...event };
              singleEvent.recurringType = null;
              singleEvent.parentEventId = null;
              singleEvent.createdByStaffId = singleEvent.createdByStaffId || this.state.authModule.user.id

              console.log('singleEvent', singleEvent)

              return await firebase
                .firestore()
                .collection("calender")
                .add(singleEvent).then(async (createdDocRef: any) => {
                  console.log('single event created')
                  if (event.isExternallyHosted && event.isPublished && (event.hostEmailAddress != prevEventData.hostEmailAddress || event.isPublished != prevEventData.isPublished)) {
                    await dispatch('sendEmailToExternalHostOfEvent', { eventId: createdDocRef.id, parentEventId: null })
                  }

                  commit("setCurrentCalenderRecurringEvents", []);

                  return await dispatch("getCalenderEvent", createdDocRef.id).then(() => {
                    return createdDocRef.id
                  })

                }).catch(e => { console.log('single event creation error:', e); return false })
            })
        }
        else {
          console.log('updating single event')

          return await firebase
            .firestore()
            .collection("calender")
            .doc(event.id)
            .update({
              ...event,
              start: firebase.firestore.Timestamp.fromDate(new Date(event.start)),
              end: firebase.firestore.Timestamp.fromDate(new Date(event.end)),
              VideoLink: event.VideoLink ?? ""
            })
            .then(async () => {
              if (event.isExternallyHosted && event.isPublished && (event.hostEmailAddress != prevEventData.hostEmailAddress || event.isPublished != prevEventData.isPublished)) {
                await dispatch('sendEmailToExternalHostOfEvent', { eventId: event.id, parentEventId: null })
              }

              dispatch("getCalenderEvent", event.id);

              console.log('updated single event')

              return event.id
            });
        }
      }
    },

    async updateCurrentAndfollowingEvents({ commit, dispatch }, eventData) {
      const event = eventData.event;
      const prevEventData = (
        await firebase
          .firestore()
          .collection("calender")
          .doc(event.id).get()).data();

      const recurringRange = eventData.recurringRange;

      //update events
      const startEventRangeDate = moment(recurringRange.start).startOf('date').toDate()
      const endEventRangeDate = moment(recurringRange.end).endOf('date').toDate()
      console.log('startEventRangeDate', startEventRangeDate);
      console.log('endEventRangeDate', endEventRangeDate);

      const query = firebase
        .firestore()
        .collection("calender")
        .where("parentEventId", "==", event.parentEventId)
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">=", startEventRangeDate)
        .where("start", "<=", endEventRangeDate)

      let prevRecurringEvents = [];

      return await query
        .get()
        .then(recurringEvents => {
          console.log(`recurringEvents(${recurringEvents.docs.length})`, recurringEvents.docs)
          prevRecurringEvents = serializer.querySnapshotToDocuments(recurringEvents);

          const batch = firebase.firestore().batch();
          recurringEvents.docs.forEach(doc => {
            batch.delete(doc.ref);
          });
          batch.commit();
        })
        .then(async () => {
          event.startDate = recurringRange.start;
          event.endDate = recurringRange.end;
          event.duration = recurringRange.duration;
          event.eventType = event.type;

          return await dispatch("getRecurringEvents", {
            eventFormData: event,
            noEndDate: event.noEndDate,
            invitees: event.invitees,
            likes: event.likes,
            monthlyRepeatOn: event.monthlyRepeatOn
          }).then(async rEvents => {

            rEvents.forEach(item => {
              const eventStart = moment(item.start).format("YYYY-MM-DD");
              const currentEventStart = moment(recurringRange.start).format("YYYY-MM-DD");
              const prevEvent = prevRecurringEvents.find(x => moment(x.start.seconds * 1000).format("YYYY-MM-DD") === eventStart);

              if(eventStart !== currentEventStart) {
                item.attendees = prevEvent?.attendees ? prevEvent?.attendees : [];
                item.attendeesIds = prevEvent?.attendeesIds ? prevEvent?.attendeesIds : [];
                item.galleryPhotos = prevEvent?.galleryPhotos ? prevEvent?.galleryPhotos : [];
              }
            })

            //send email to external host of event
            if (rEvents[0].isExternallyHosted && rEvents[0].isPublished && (rEvents[0].hostEmailAddress != prevEventData.hostEmailAddress || rEvents[0].isPublished != prevEventData.isPublished)) {
              await dispatch('sendEmailToExternalHostOfEvent', { eventId: null, parentEventId: rEvents[0].parentEventId })
            }

            return await dispatch("createNewCalenderEvents", {
              events: rEvents,
              isUpdateRecurringEventsAction: true
            }).then(async (createdEvents) => {

              if(event.office365EventId?.length > 0){

                const seriesResponse = await dispatch('getMsEventSeries', { id: event.office365EventId, facilityId: event.facilityId, 
                  startDateTime: moment(recurringRange.start).format('YYYY-MM-DDTHH:mm:ss.SSSSSSS'), endDateTime: moment(recurringRange.end).add(1, "day").format('YYYY-MM-DDTHH:mm:ss.SSSSSSS') })

                console.log('event: ', event)
                  const params = {
                    id: null,
                    subject: event.name,
                    roomName: event.office365RoomName,
                    startDateTime: moment(event.start).format('YYYY-MM-DDTHH:mm:ss'),
                    endDateTime: moment(event.end).format('YYYY-MM-DDTHH:mm:ss'),
                    attendees: [ { email: this.state.office365Module.office365TokenData.email, name: this.state.office365Module.office365TokenData.name }],
                    office365ResourceEmail: event?.office365Email || null,
                    facilityId: event.facilityId,
                    recurrence: {
                        type: event.recurringType,
                        repeatType: event?.recurringRepeatType,
                        repetitionsDays: event?.recurringRepetitionsDays,
                        monthlyRepeatOn: event.monthlyRepeatOn,
                        interval: event.recurringRepetitions,
                        startDateTime: recurringRange.start,
                        endDateTime: recurringRange.end
                    }
                }

                for (const instance of seriesResponse.data?.data?.value) {

                  console.log('update ms event: ', instance.id)

                  const instanceParams = {...params, id: instance.id}

                  const newTimeArray = event.duration.split('-')
                  const newStartTime = newTimeArray[0].split(':')
                  const newEndTime = newTimeArray[1].split(':')

                  instanceParams.startDateTime = moment(instance.start.dateTime).set({ hour: newStartTime[0], minute: newStartTime[1], second: 0}).format('YYYY-MM-DDTHH:mm:ss');
                  instanceParams.endDateTime = moment(instance.end.dateTime).set({ hour: newEndTime[0], minute: newEndTime[1], second: 0}).format('YYYY-MM-DDTHH:mm:ss');
                  
                  /*console.log('instanceParams', JSON.stringify(instanceParams))*/

                  await dispatch('updateMsEvent', instanceParams).then(response => {
                    if(response.data?.status === 200){
                      console.log('MS event updated', instance.id)
                    }
                    else if (response.data?.status !== 200) {
                        console.error('Error updating MS event', response.data)
                    }
                });
                    
              }
            }

              await dispatch("getCalenderRecurringEvents", createdEvents[0].id)

              return await dispatch("getCalenderEvent", createdEvents[0].id).then(() => {
                console.log('updated recurring upcoming events')
                return createdEvents[0].id
              })
            })

          })
        })

    },

    async publishConciergeServices(_, draftEvents) {
      const draftEventsIds = draftEvents.map(x => x.id);

      const conciergeServicesIds: string[] = Array.from(new Set(
        draftEvents
          .filter((x: any) => x.type === EventsTypes.PremiumServices)
          .map((x: any) => x.serviceId)
      ));

      if(conciergeServicesIds?.length) {
        const conciergeServices = [];
        for(let i = 0; i < conciergeServicesIds.length; i++) {
          await firebase
            .firestore()
            .collection("additionalServices")
            .doc(conciergeServicesIds[i])
            .get()
            .then((documentSnapshot) => {
              conciergeServices.push(serializer.documentSnapshotToDocument(documentSnapshot));
            })
        }

        const servicesToUpdate = conciergeServicesIds.map(id => {
          const events = conciergeServices.find(x => x.id === id)?.events ?? [];

          events.forEach((event) => {
            if(draftEventsIds.includes(event.id)) {
              event.isPublished = true;
            }
          })

          return {
            docId: id,
            updates: {
              isPublished: true,
              events: events
            }
          }
        })

        await batchUpdate('additionalServices', servicesToUpdate);
      }
    },

    async publishEvents({ commit, dispatch }, dates) {

      const draftEvents = await getDraftEventsByTimeframe(this.state.authModule.currentFacility, dates);

      const itemsToUpdate = draftEvents.map(event => {
        const itemToUpdate = { docId: event.id, updates: { isPublished: true, scheduledPublishDateTime: null, dateTimeUpdated: firebase.firestore.Timestamp.fromDate(new Date()) } }
        return itemToUpdate
      })

      const result = await batchUpdate('calender', itemsToUpdate);

      //update events on the current (month) calendar screen if published events belongs to the current month
      if (result.code == 'Success' && moment().month() === moment(dates[0]).month()) {
        const start = moment().add(-1, 'months').toDate()
        const end = moment().add(1, 'months').toDate();
        await dispatch("publishConciergeServices", draftEvents);
        return await dispatch('getCalenderEvents', { start: start, end: end });
      }

      return;
    },

    async sendEmailToExternalHostOfEvent({ dispatch }, { eventId, parentEventId }) {
      firebase.app().functions(getRegionName())
        .httpsCallable("sendEmailToExternalHostOfEvent")({
          eventId: eventId,
          parentEventId: parentEventId
        })
        .then(snap => {
          console.log(
            "email sent to external host of recurring event: " + snap
          );
        });
    },

    async updateSingleCalenderEvent({ dispatch }, { event, id, recurringRange }) {

      const prevEventData = (
        await firebase
          .firestore()
          .collection("calender")
          .doc(event.id)
          .get()
      ).data();

      if (event.start) {
        event.startISOString = event.start.toISOString()
      }

      return await firebase
        .firestore()
        .collection("calender")
        .doc(event.id)
        .update({
          ...event,
          start: firebase.firestore.Timestamp.fromDate(new Date(event.start)),
          end: firebase.firestore.Timestamp.fromDate(new Date(event.end)),
          VideoLink: event.VideoLink ?? ""
        })
        .then(async () => {

          const response: any = { msEventUpdateResult: null, eventResult: { status: 200 } }

          try {
            if (event.isExternallyHosted && event.isPublished && (event.hostEmailAddress != prevEventData.hostEmailAddress || event.isPublished != prevEventData.isPublished)) {
               dispatch('sendEmailToExternalHostOfEvent', { eventId: event.id, parentEventId: null })
               dispatch("getCurrentSocialProgramEvents", { socialClubId: id });
            }
          } catch (error) {
            console.error('sendEmailToExternalHostOfEvent error: ', error)
          }
  
          try {
            if (event?.office365EventId?.length > 0) {

              let msEventId = null

              if(recurringRange){
                const seriesResponse = await dispatch('getMsEventSeries', { id: event.office365EventId, facilityId: event.facilityId, 
                  startDateTime: moment(recurringRange.start).format('YYYY-MM-DDTHH:mm:ss.SSSSSSS'), endDateTime: moment(recurringRange.end).format('YYYY-MM-DDTHH:mm:ss.SSSSSSS') })

                //find event in series to get id
                msEventId = seriesResponse.data?.data?.value.find(x => x.start.dateTime === moment(prevEventData.start.seconds * 1000).format('YYYY-MM-DDTHH:mm:ss.SSSSSSS') && x.end.dateTime === moment(prevEventData.end.seconds * 1000).format('YYYY-MM-DDTHH:mm:ss.SSSSSSS'))?.id
              }
              else{
                msEventId = event?.office365EventId
              }

              if(msEventId?.length > 0){
                const params = {
                  id: msEventId,
                  subject: event.name,
                  roomName: event.office365RoomName,
                  startDateTime: moment(event.start).format('YYYY-MM-DDTHH:mm:ss'),
                  endDateTime: moment(event.end).format('YYYY-MM-DDTHH:mm:ss'),
                  attendees: [{email: this.state.office365Module.office365TokenData.email, name: this.state.office365Module.office365TokenData.name}],
                  office365ResourceEmail: event?.office365Email || null,
                  facilityId: event.facilityId,
                }
                const msEventUpdateResult = await dispatch('updateMsEvent', params)
                response.msEventUpdateResult = msEventUpdateResult?.data
              }
              else{
                response.msEventUpdateResult.status = 400
              }
            }
          } catch (error) {
            console.error('updateMsEvent error: ', error)
          }

          return response
        });
    },

    async updateSingleCalenderEventVirtual({ dispatch }, event) {

      if (event.start) {
        event.startISOString = event.start.toISOString()
      }

      firebase
        .firestore()
        .collection("calender")
        .doc(event.id)
        .update({
          ...event
        });
    },

    async deleteCalenderEvent({ dispatch }, {id, isRecurring, office365EventId, range}) {

      const eventSnap = await firebase.firestore().collection("calender").doc(id).get();

      if(!eventSnap.exists) return

      const eventData = eventSnap.data()

      await firebase.firestore().collection("calender").doc(id).delete();
          if(office365EventId) {

            if(isRecurring === true){

              //delete one event from series
              const seriesResponse = (await dispatch('getMsEventSeries', { id: office365EventId, facilityId: this.state.authModule.currentFacility, 
                startDateTime: moment(eventData.start.toDate()).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSSSSS'), 
                endDateTime: moment(eventData.end.toDate()).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSSSSS') }))

              if(seriesResponse.data && seriesResponse.data?.data?.value.length > 0) {

                const eventIdFromRecurrence = seriesResponse.data.data.value[0].id

                await dispatch('deleteMsEvents', { ids: [eventIdFromRecurrence] }).then(response => {
                if (response.data?.status !== 200) {
                    console.error('deleteMsEvents error: ', response.data)
                }});
              }
            }
            else{
              //delete not recurring event
              await dispatch('deleteMsEvents', { ids: [office365EventId] }).then(response => {
                if (response.data?.status !== 200) {
                    console.error('deleteMsEvents error: ', response.data)
                }});
            }
          }

      if (range) {
        await dispatch("getCalenderEvents", range);
      } else {
        await dispatch("getCalenderEvents");
      }
    },

    async deleteCalenderEventSeries({ dispatch }, { parentEventId, deleteUpcomingEvents, range }) {
      const today = moment().toDate();
      let query = firebase
        .firestore()
        .collection("calender")
        .where("parentEventId", "==", parentEventId)
        .where("facilityId", "==", this.state.authModule.currentFacility)

      //delete upcoming event series
      if (!deleteUpcomingEvents) {
        query = query.where("end", ">=", today)
      }

      return await query.get()
        .then(async (recurringEvents: any) => {

            const batch = firebase.firestore().batch();

            for (const doc of recurringEvents.docs) {
              await batch.delete(doc.ref);
            }

            await batch.commit();

            if (range) {
              await dispatch("getCalenderEvents", range);
            } else {
              await dispatch("getCalenderEvents");
            }

            const anyRecEvent = recurringEvents.docs[0].data()

            if(anyRecEvent.office365EventId) {
                await dispatch('deleteMsEvents', { ids: [anyRecEvent.office365EventId] }).then(response => {
                if (response.data?.status !== 200) {
                    console.error('deleteMsEvents error: ', response.data)
                }});
              }

            return true
          });
    },

    async deleteCalenderEventSeriesByRange({ dispatch }, { parentEventId, start, end }) {

    return  await firebase
      .firestore()
      .collection("calender")
      .where("parentEventId", "==", parentEventId)
      .where("facilityId", "==", this.state.authModule.currentFacility)
      .where("start", ">=", start)
      .where("start", "<=", end)
      .get()
      .then(async (querySnapshot: any) => {
          for (const event of querySnapshot.docs) {
              await firebase.firestore().collection('calender').doc(event.id).delete()
          }

          const anyRecEvent = querySnapshot.docs[0].data()

          if(anyRecEvent.office365EventId) {

            const sortedDates = querySnapshot.docs.map(doc => {
              const eventData = doc.data(); return { start: eventData.start.toDate(), end: eventData.end.toDate() }})
              .sort((a, b) => a.start - b.start);

            const seriesResponse = (await dispatch('getMsEventSeries', { id: anyRecEvent.office365EventId, facilityId: anyRecEvent.facilityId, 
              startDateTime: moment(sortedDates[0].start).format('YYYY-MM-DDTHH:mm:ss.SSSSSSS'), 
              endDateTime: moment(sortedDates[sortedDates.length - 1].end).add(1, "day").format('YYYY-MM-DDTHH:mm:ss.SSSSSSS') }))

              if(seriesResponse.data && seriesResponse.data?.data?.value.length > 0) {

                const ids = seriesResponse.data.data.value.map(x => x.id)

                await dispatch('deleteMsEvents', { ids: ids }).then(response => {
                if (response.data?.status !== 200) {
                    console.error('deleteMsEvents error: ', response.data)
                }});
              }
              
          }
      })
    },

    async cancelScheduldCall({ dispatch }, { id, cancelReason }) {
      return await firebase.firestore().collection("calender").doc(id).update({
        cancelledByStaffId: this.state.authModule.user.id,
        cancelReason: cancelReason || null
      })
    },

    async createNewCalenderEvents({ commit }, { events, isUpdateRecurringEventsAction = false }) {
      const ids = [];
      const batchArray = [];
      batchArray.push(firebase.firestore().batch());
      let operationCounter = 0;
      let batchIndex = 0;

      for (let i = 0; i < events.length; i++) {
        operationCounter++;
        const event = events[i]
        try {
          const eventRef = firebase.firestore().collection('calender').doc();
          console.log(`[${i}] eventRef.id: ${eventRef.id}`,)
          batchArray[batchIndex].set(eventRef, { ...event })

          if (operationCounter === 50) {
            console.log(`batchArray.push(${batchArray.length})`)
            batchArray.push(firebase.firestore().batch());
            batchIndex++;
            operationCounter = 0;
          }

          ids.push(eventRef.id);
        } catch (error) {
          console.error('batch set error: ', error);
        }
      }

      try {
        for (let index = 0; index < batchArray.length; index++) {
          const batch = batchArray[index]
          if (batch && batch.Ed && batch.Ed.length) {
            console.log(`batch[${index}].commit(${batch.Ed.length})`)
          }
          await batch.commit()
        }
      } catch (error) {
        console.error('batch commit error: ', error)
      }

      return await this.dispatch("getWhereIdInIds", {
        ids: ids, path: 'calender'
      }).then(async newEvents => {
        //send email to external host of event
        if (events[0].isPublished && events[0].isExternallyHosted && !isUpdateRecurringEventsAction) {
          const parentEventId = events[0].parentEventId;
          this.dispatch('sendEmailToExternalHostOfEvent', { eventId: null, parentEventId: parentEventId })
        }

        newEvents = newEvents.sort((a: any, b: any) => moment(a.start.seconds * 1000).diff(moment(b.start.seconds * 1000)));

        this.dispatch("getCalenderEvents");

        commit("setRecurringEventsUpdated", newEvents);

        return newEvents
      });
    },

    async updateCalendarEvents(context, events) {
      const calendar = firebase.firestore().collection("calender");

      calendar
        .where("parentEventId", "==", events[0].parentEventId)
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .get()
        .then(snapshots => {
          if (snapshots.size > 0) {
            snapshots.forEach(event => {
              calendar.doc(event.id).update({
                ...event
              });
            });
          }
        });
    },

    getWhereIdInIds(context, { ids, path }) {
      // don't run if there aren't any ids or a path for the collection
      if (!ids || !ids.length || !path) return [];
      const collectionPath = firebase.firestore().collection(path);
      const batches = [];
      while (ids.length) {
        // firestore limits batches to 10
        const batch = ids.splice(0, 10);
        // add the batch request to to a queue
        batches.push(
          collectionPath
            .where('facilityId', '==', this.state.authModule.currentFacility)
            .where(firebase.firestore.FieldPath.documentId(), 'in', [...batch])
            .get()
            .then((results: any) => results.docs.map((result: any) => ({ id: result.id, ...result.data() })))
        )
      }
      // after all of the data is fetched, return it
      return Promise.all(batches).then((content: any) => content.flat());
    },

    // this function generate array of Calendar events depending on recurring type
    async getRecurringEvents(context, { eventFormData, noEndDate, invitees, likes, monthlyRepeatOn }) {

      if (!eventFormData.facilityId) {
        eventFormData.facilityId = this.state.authModule.currentFacility;
      }

      eventFormData.createdByStaffId = eventFormData.createdByStaffId || this.state.authModule.user.id
      eventFormData.scheduledPublishDateTime = eventFormData?.scheduledPublishDateTime? eventFormData?.scheduledPublishDateTime.toISOString():null

      console.log('eventFormData: ', eventFormData)
      return await firebase.app().functions(getRegionName()).httpsCallable("groupC-generateRecurringEvents")({
        eventFormData: eventFormData,
        noEndDate: noEndDate,
        invitees: invitees,
        likes: likes,
        monthlyRepeatOn: monthlyRepeatOn
      }).then((result: any) => {
        const events = result.data.events.map(x => {
          x.start = new Date(x.start)
          x.end = new Date(x.end)
          x.startISOString = new Date(x.start).toISOString()
          x.scheduledPublishDateTime = x.scheduledPublishDateTime? new Date(x.scheduledPublishDateTime): null

          if (x.likes && Array.isArray(x.likes)) {
            x.likes = x.likes.map(like => ({
                ...like,
                date: new Date(like.date)
            }));
          }

          return x
        });

        return events;
      }).catch((error: any) => {
        console.log(error);
        return [];
      })

    },

    // this function map Calendar item from CreateEvent model
    // when we create new event from ManipulateCalendarEventDialog
    setupCalendarEvent(context, { newEventData }) {

      const calendarItem = new Calendar();
      const timeRange = newEventData.duration.split("-");

      const startDate = moment(newEventData.startDate).toDate();
      const endDate = moment(newEventData.endDate).toDate();

      startDate.setHours(
        parseInt(timeRange[0].split(":")[0]),
        parseInt(timeRange[0].split(":")[1])
      );

      endDate.setHours(
        parseInt(timeRange[1].split(":")[0]),
        parseInt(timeRange[1].split(":")[1])
      );

      // parentEventId - id to link series of events between each other
      calendarItem.parentEventId = newEventData.isRecurring ? newEventData.parentEventId : "";
      calendarItem.facilityId = this.state.authModule.currentFacility;
      calendarItem.name = newEventData.name;
      calendarItem.location = newEventData.location;
      calendarItem.description = newEventData.description;
      calendarItem.going = newEventData.going ?? [];
      calendarItem.goingDetails = newEventData.goingDetails ?? [];
      calendarItem.conciergeRequestId = newEventData.conciergeRequestId ?? null;
      calendarItem.isRecurring = newEventData.isRecurring;
      calendarItem.isVirtualEvent = newEventData.isVirtualEvent;
      calendarItem.recurringType = newEventData.recurringType;
      calendarItem.noEndDate = newEventData.noEndDate;
      calendarItem.residentCanSeeGuestList = newEventData.residentCanSeeGuestList;
      calendarItem.VideoLink = newEventData.VideoLink;
      calendarItem.imageUrl = newEventData.imageUrl;
      calendarItem.thumbnailUrl = newEventData.thumbnailUrl;
      calendarItem.galleryPhotos = newEventData.galleryPhotos;
      calendarItem.start = firebase.firestore.Timestamp.fromDate(startDate);
      calendarItem.end = firebase.firestore.Timestamp.fromDate(endDate);
      calendarItem.type = newEventData.type;
      calendarItem.invitees = newEventData.invitees;
      calendarItem.isExternallyHosted = newEventData.isExternallyHosted;
      calendarItem.hostEmailAddress = newEventData.hostEmailAddress;
      calendarItem.channelId = newEventData.channelId;
      calendarItem.attendees = newEventData.attendees ?? [];
      calendarItem.attendeesIds = newEventData.attendeesIds ?? [];
      calendarItem.participantLimit = newEventData.participantLimit ?? "";
      calendarItem.participantLimitNumber = newEventData?.participantLimitNumber ?? null;
      calendarItem.isWaitingList = newEventData.isWaitingList ?? false;
      calendarItem.startISOString = moment(calendarItem.start.seconds * 1000).toISOString()
      calendarItem.allDay = newEventData.allDay ?? null
      calendarItem.isPublished = newEventData.isPublished ?? null 
      calendarItem.scheduledPublishDateTime = newEventData.scheduledPublishDateTime ?? null
      calendarItem.wing = newEventData.wing ?? null
      calendarItem.wingsIds = newEventData.wingsIds ?? null
      calendarItem.createdByStaffId = newEventData.createdByStaffId ?? this.state.authModule.user.id
      calendarItem.isDisplayInWeekSummary = newEventData.isDisplayInWeekSummary ?? false
      calendarItem.office365EventId = newEventData.office365EventId ?? null

      if (newEventData.duplicatedFrom) {
        calendarItem.duplicatedFrom = newEventData.duplicatedFrom;
      }

      return calendarItem;
    },

    // this function map Calendar model to CreateEvent
    // when we click on edit event from ManipulateCalendarEventDialog component
    setupCreateEvent(context, calendarData) {
      const eventData = new CreateEvent();
      const startTime = calendarData.start.toTimeString().substr(0, 5);
      const endTime = calendarData.end.toTimeString().substr(0, 5);

      // todo : add needed properties
      eventData.parentEventId = calendarData.isRecurring
        ? calendarData.parentEventId
        : "";
      eventData.facilityId = this.state.authModule.currentFacility;
      eventData.name = calendarData.name;
      eventData.location = calendarData.location;
      eventData.description = calendarData.description;
      eventData.isRecurring = calendarData.isRecurring;
      eventData.conciergeRequestId = calendarData.conciergeRequestId ?? null;
      eventData.going = calendarData.going ?? [];
      eventData.isVirtualEvent = calendarData.isVirtualEvent;
      eventData.isExternallyHosted = calendarData.isExternallyHosted;
      eventData.recurringType = calendarData.recurringType;
      eventData.noEndDate = calendarData.noEndDate;
      eventData.residentCanSeeGuestList = calendarData.residentCanSeeGuestList;
      eventData.VideoLink = calendarData.VideoLink;
      eventData.imageUrl = calendarData.imageUrl;
      eventData.startDate = moment(calendarData.start).format("YYYY-MM-DD");
      eventData.endDate = moment(calendarData.end).format("YYYY-MM-DD");
      eventData.invitees = calendarData.invitees;
      eventData.type = calendarData.type;
      eventData.duration = `${startTime}-${endTime}`;
      eventData.channelId = calendarData.channelId;
      eventData.allDay = calendarData.allDay;
      eventData.createdByStaffId = calendarData.createdByStaffId || this.state.authModule.user.id

      if (calendarData.duplicatedFrom) {
        eventData.duplicatedFrom = calendarData.duplicatedFrom;
      }

      return eventData;
    },

    async getResidentEventsMonth({ commit }, { residentId, duration }) {
      const toDate = moment().toDate();
      const fromDate = moment()
        .add(-duration, "days")
        .toDate();

      const calenderEvents = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">=", fromDate)
        .where("start", "<=", toDate)
        .where("attendeesIds", "array-contains", residentId);

      await calenderEvents.get().then(querySnapshot => {
        const resList: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          thisRes.start = moment(thisRes.start.seconds * 1000).toDate();
          thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
          thisRes.scheduledPublishDateTime = thisRes?.scheduledPublishDateTime? new Date(thisRes.scheduledPublishDateTime): null

          resList.push(thisRes);
        });
        commit("setResidentEventsMonth", resList);
      });
    },

    async getResidentVideoCallsMonth({ commit }, { residentId, duration }) {
      console.log("getResidentVideoCallsMonth");
      const toDate = moment().toDate();
      const fromDate = moment()
        .add(-duration, "days")
        .toDate();
      const eventsByInvitees: Array<any> = [];
      const eventsById: Array<any> = [];

      const videocallResidentByInvitees = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">=", fromDate)
        .where("start", "<=", toDate)
        .where("type", "==", EventsTypesMinor.ScheduledCall)
        .where("familyCallInvitees", "array-contains", residentId);

      await videocallResidentByInvitees.get().then(querySnapshot => {
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          thisRes.start = moment(thisRes.start.seconds * 1000).toDate();
          thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
          thisRes.scheduledPublishDateTime = thisRes?.scheduledPublishDateTime? moment(thisRes.scheduledPublishDateTime.seconds * 1000).toDate(): null;

          eventsByInvitees.push(thisRes);
        });
      });

      const videocallResidentById = firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("start", ">=", fromDate)
        .where("start", "<=", toDate)
        .where("type", "==", EventsTypesMinor.ScheduledCall)
        .where("createdByResidentId", "==", residentId);

      await videocallResidentById.get().then(querySnapshot => {
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          thisRes.start = moment(thisRes.start.seconds * 1000).toDate();
          thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
          thisRes.scheduledPublishDateTime = thisRes?.scheduledPublishDateTime? moment(thisRes.scheduledPublishDateTime.seconds * 1000).toDate(): null;

          eventsById.push(thisRes);
        });
      });

      await commit("setResidentVideoCallsMonth", [
        ...eventsByInvitees,
        ...eventsById
      ]);
    },
    
    async unsubscribeOnCalenderEvents({state}) {
      if (state.onCalenderEventsUnsubscribe) {
        console.log("unsubscribe onCalenderEvents");
        state.onCalenderEventsUnsubscribe();
        state.onCalenderEventsUnsubscribe = null
      }
    }
  },
  getters: {
    calenderEventsLifestyle: state => {
      return state.calenderEventsUpcoming.filter(
        event => event.type === EventsTypes.LifestylePrograms
      );
    },
    calenderEventsPremium: state => {
      return state.calenderEventsUpcoming.filter(
        event => event.type === EventsTypes.PremiumServices
      );
    },
    calenderEventsScheduledCall: state => {
      return state.calenderEventsUpcoming.filter(
        event => event.type === EventsTypesMinor.ScheduledCall
      );
    }
  }
};
export default calenderModule;
